import { createReducer, on } from '@ngrx/store';
import * as Actions from '../actions/pd.actions';
import { User } from '../../models/user.model';
import { Breadcrumb } from '../../models/breadcrumbs.model';
import { Role } from '../../models/role.model';
import { isSelectedOrganizationFilters, isSelectedProgramFilters, SelectedOrganizationFilters, SelectedProgramFilters } from '../../models/filters.model';
import { Program, Product } from '../../models/program.model';
import { ProgramOrganizationType } from '../../models/program-organization.model';
import { Organization, Site } from '../../models/organization.model';
import { PerformanceInterval } from '../../models/performance-chart.model';
import { PerformanceEvent, PerformanceEventKind } from '../../models/performance-event.model';
import { RegistrationTableRow } from '../../models/performance-view.model';

export type ResourceKey =
  | 'user'
  | 'roles'
  | 'filters'
  | 'organization'
  | 'products'
  | 'sitePerformanceEvents'
  | 'sitePerformanceIntervalData'
  | 'productPerformanceEvents'
  | 'productSitePerformances';

export interface RequestState {
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

export interface State {
  user: User | null;
  roles: Role[];
  activeTabIndex: number;
  viewMode: ProgramOrganizationType;
  breadcrumbs: Breadcrumb[];
  programs: Program[];
  products: Product[];
  sitePerformanceEvents: PerformanceEvent[];
  productPerformanceEvents: PerformanceEvent[];
  productSitePerformances: RegistrationTableRow[];
  organization: Organization[];
  sites: Site[];
  programFilters?: SelectedProgramFilters;
  organizationFilters?: SelectedOrganizationFilters;
  selectedPerformanceEvent: SelectedPerformanceEvent | null;
  sitePerformanceIntervalData?: PerformanceInterval[];
  requests: Record<ResourceKey, RequestState>;
}

const blankReq: RequestState = { loading: false, loaded: false, error: null };

export interface SelectedPerformanceEvent {
  kind: PerformanceEventKind;
  eventId: string;
  siteId?: string;
  productId?: string;
}

export const initialState: State = {
  user: null,
  roles: [],
  activeTabIndex: 0,
  breadcrumbs: [],
  programs: [],
  products: [],
  sitePerformanceEvents: [],
  productPerformanceEvents: [],
  productSitePerformances: [],
  organization: [],
  sites: [],
  programFilters: undefined,
  organizationFilters: undefined,
  viewMode: ProgramOrganizationType.Program,
  selectedPerformanceEvent: null,
  sitePerformanceIntervalData: [],
  requests: {
    user: { ...blankReq },
    roles: { ...blankReq },
    filters: { ...blankReq },
    organization: { ...blankReq },
    products: { ...blankReq },
    sitePerformanceEvents: { ...blankReq },
    sitePerformanceIntervalData: { ...blankReq },
    productPerformanceEvents: { ...blankReq },
    productSitePerformances: { ...blankReq },
  },
};

export const Reducer = createReducer(
  initialState,

  on(Actions.userReceived, (state: State, { user }) => ({
    ...state,
    ...setSuccess(state, 'user'),
    user: user,
  })),
  on(Actions.rolesReceived, (state: State, { roles }) => ({
    ...state,
    ...setSuccess(state, 'roles'),
    roles,
  })),
  on(Actions.setActiveTab, (state: State, { tabIndex }) => ({
    ...state,
    activeTabIndex: tabIndex,
  })),
  on(Actions.setViewMode, (state, { viewMode }) => ({ ...state, viewMode })),
  on(Actions.setBreadcrumbs, (state: State, { breadcrumbs }) => ({
    ...state,
    breadcrumbs: breadcrumbs,
  })),
  on(Actions.loadPrograms, (state: State) => setLoading(state, 'filters')),
  on(Actions.programsLoaded, (state: State, { programs, defaultFilters }) => ({
    ...state,
    ...setSuccess(state, 'filters'),
    programs: programs,
    programFilters: state.programFilters ?? defaultFilters,
  })),
  on(Actions.loadProgramsError, (state: State, { error }) => setFailure(state, 'filters', error)),
  on(Actions.loadOrganizations, (state: State) => setLoading(state, 'organization')),
  on(Actions.organizationsLoaded, (state: State, { organizations, defaultFilters }) => ({
    ...state,
    ...setSuccess(state, 'organization'),
    organization: organizations,
    organizationFilters: state.organizationFilters ?? defaultFilters,
  })),
  on(Actions.loadOrganizationsError, (state: State, { error }) => setFailure(state, 'organization', error)),
  on(Actions.loadSitesForOrganization, (state: State) => ({ ...state, sites: [] })),
  on(Actions.sitesForOrganizationLoaded, (state: State, { sites }) => ({
    ...state,
    sites,
  })),
  on(Actions.loadSitesForOrganizationError, (state: State, { error }) => {
    console.error('Error loading sites:', error);
    return state;
  }),
  on(Actions.loadProductsForProgram, (state: State) => ({ ...state, ...setLoading(state, 'products'), products: [] })),
  on(Actions.productsForProgramLoaded, (state: State, { products }) => ({
    ...state,
    ...setSuccess(state, 'products'),
    products,
  })),
  on(Actions.loadProductsForProgramError, (state: State, { error }) => setFailure(state, 'products', error)),
  on(Actions.setFilters, (state: State, { filters }) => {
    if (isSelectedOrganizationFilters(filters)) {
      return { ...state, organizationFilters: filters };
    }
    if (isSelectedProgramFilters(filters)) {
      return { ...state, programFilters: filters };
    }
    return state;
  }),
  // Site performances
  on(Actions.loadSitePerformanceEvents, (state: State) => setLoading(state, 'sitePerformanceEvents')),
  on(Actions.sitePerformanceEventsLoaded, (state: State, { performances }) => ({
    ...state,
    ...setSuccess(state, 'sitePerformanceEvents'),
    sitePerformanceEvents: performances,
  })),
  on(Actions.loadSitePerformanceEventsError, (state: State, { error }) => setFailure(state, 'sitePerformanceEvents', error)),
  on(Actions.selectPerformanceEvent, (state: State, { kind, eventId, siteId, productId }) => ({
    ...state,
    selectedPerformanceEvent: { kind, eventId, siteId, productId },
    sitePerformanceIntervalData: [],
    productSitePerformances: [],
  })),
  // Site performance interval data
  on(Actions.loadSitePerformanceIntervalData, (state: State) => setLoading(state, 'sitePerformanceIntervalData')),
  on(Actions.sitePerformanceIntervalDataLoaded, (state: State, { performanceData }) => ({
    ...state,
    ...setSuccess(state, 'sitePerformanceIntervalData'),
    sitePerformanceIntervalData: performanceData,
  })),
  on(Actions.loadSitePerformanceIntervalDataError, (state: State, { error }) => setFailure(state, 'sitePerformanceIntervalData', error)),
  on(Actions.loadProductPerformanceIntervalData, (state: State) => setLoading(state, 'sitePerformanceIntervalData')),
  // Product performances
  on(Actions.loadProductPerformanceEvents, (state: State) => setLoading(state, 'productPerformanceEvents')),
  on(Actions.productPerformanceEventsLoaded, (state: State, { performances }) => ({
    ...state,
    ...setSuccess(state, 'productPerformanceEvents'),
    productPerformanceEvents: performances,
  })),
  on(Actions.loadProductPerformanceEventsError, (state: State, { error }) => setFailure(state, 'productPerformanceEvents', error)),
  on(Actions.loadProductSitePerformances, (state: State) => setLoading(state, 'productSitePerformances')),
  on(Actions.productSitePerformancesLoaded, (state: State, { performances }) => ({
    ...state,
    ...setSuccess(state, 'productSitePerformances'),
    productSitePerformances: performances,
  })),
  on(Actions.loadProductSitePerformancesError, (state: State, { error }) => setFailure(state, 'productSitePerformances', error))
);

// Helper functions kept for future scale
function setLoading(state: State, key: ResourceKey): State {
  return {
    ...state,
    requests: {
      ...state.requests,
      [key]: { loading: true, loaded: false, error: null },
    },
  };
}

function setSuccess(state: State, key: ResourceKey): State {
  return {
    ...state,
    requests: {
      ...state.requests,
      [key]: { loading: false, loaded: true, error: null },
    },
  };
}

function setFailure(state: State, key: ResourceKey, error: string): State {
  return {
    ...state,
    requests: {
      ...state.requests,
      [key]: { loading: false, loaded: false, error },
    },
  };
}
